import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { supabase } from "../lib/supabaseClient";
import jsPDF from "jspdf";

export default function EstimationSuccession() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const locale = useMemo(() => {
    const language = i18n.resolvedLanguage || i18n.language || "fr";
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
  }, [i18n.language, i18n.resolvedLanguage]);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }),
    [locale]
  );
  const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
  const formatDate = (value = new Date()) => new Date(value).toLocaleDateString(locale);

  const [successionPlans, setSuccessionPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planItems, setPlanItems] = useState([]);
  const [heritiers, setHeritiers] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultDate = `${new Date().getFullYear() - 1}-12-31`;
  const [formData] = useState({
    date_simulation: defaultDate,
    expert_consulte: "",
    notes_particulieres: "",
    abattement_applique: 100000,
  });

  const [calculGlobal, setCalculGlobal] = useState({
    valeur_totale_collection: 0,
    droits_totaux_estimes: 0,
    repartition_par_heritier: [],
    dation_recommandee: false,
  });

  const calculerDroitsSuccession = (montant, lienParente = "enfant", abattement = 100000) => {
    let montantTaxable = Math.max(0, montant - abattement);
    let droits = 0;

    if (lienParente === "conjoint" || lienParente === "Conjoint") return 0;

    if (lienParente === "enfant" || lienParente === "Fille" || lienParente === "Fils") {
      if (montantTaxable <= 8072) droits = montantTaxable * 0.05;
      else if (montantTaxable <= 12109) droits = 8072 * 0.05 + (montantTaxable - 8072) * 0.10;
      else if (montantTaxable <= 15932) droits = 8072 * 0.05 + (12109 - 8072) * 0.10 + (montantTaxable - 12109) * 0.15;
      else if (montantTaxable <= 552324) droits = 8072 * 0.05 + (12109 - 8072) * 0.10 + (15932 - 12109) * 0.15 + (montantTaxable - 15932) * 0.20;
      else if (montantTaxable <= 902838) droits = 8072 * 0.05 + (12109 - 8072) * 0.10 + (15932 - 12109) * 0.15 + (552324 - 15932) * 0.20 + (montantTaxable - 552324) * 0.30;
      else if (montantTaxable <= 1805677) droits = 8072 * 0.05 + (12109 - 8072) * 0.10 + (15932 - 12109) * 0.15 + (552324 - 15932) * 0.20 + (902838 - 552324) * 0.30 + (montantTaxable - 902838) * 0.40;
      else droits = 8072 * 0.05 + (12109 - 8072) * 0.10 + (15932 - 12109) * 0.15 + (552324 - 15932) * 0.20 + (902838 - 552324) * 0.30 + (1805677 - 902838) * 0.40 + (montantTaxable - 1805677) * 0.45;
    } else if (lienParente === "frere_soeur" || lienParente === "Famille") {
      montantTaxable = Math.max(0, montant - 15932);
      droits = montantTaxable <= 24430 ? montantTaxable * 0.35 : 24430 * 0.35 + (montantTaxable - 24430) * 0.45;
    } else {
      montantTaxable = Math.max(0, montant - 1594);
      droits = montantTaxable * 0.55;
    }

    return Math.round(droits);
  };

  const translateStatus = (status) =>
    t(`estimationSuccessionFiscal.status.${status || "draft"}`, { defaultValue: status || t("estimationSuccessionFiscal.status.draft") });

  const translateHeirType = (type) => {
    const keyMap = {
      enfant: "child",
      Fille: "daughter",
      Fils: "son",
      conjoint: "spouse",
      Conjoint: "spouse",
      frere_soeur: "sibling",
      Famille: "family",
      institution: "institution",
      Institution: "institution",
    };
    return t(`estimationSuccessionFiscal.heirTypes.${keyMap[type] || "other"}`);
  };

  const calculerRepartition = (items, plan) => {
    const valeurTotale = items.reduce((sum, item) => sum + (item.est_value || item.artworks?.estimated_value || 0), 0);
    const parHeritier = {};

    items.forEach((item) => {
      const heirId = item.heir_id;
      const heirData = plan?.beneficiaries?.find((beneficiary) => beneficiary.heir_id === heirId) || {};
      const heirName = heirData.name || t("estimationSuccessionFiscal.common.unknownHeir");
      const heirType = heirData.type || "enfant";

      if (!parHeritier[heirId]) {
        parHeritier[heirId] = {
          heir_id: heirId,
          name: heirName,
          type: heirType,
          valeur_recue: 0,
          oeuvres: [],
          droits_estimes: 0,
        };
      }

      const valeurOeuvre = item.est_value || item.artworks?.estimated_value || 0;
      parHeritier[heirId].valeur_recue += valeurOeuvre;
      parHeritier[heirId].oeuvres.push({
        title: item.artworks?.title || t("estimationSuccessionFiscal.common.untitled"),
        artist: item.artworks?.artist || "",
        valeur: valeurOeuvre,
        affinity_score: item.affinity_score,
      });
    });

    const repartition = Object.values(parHeritier).map((heir) => ({
      ...heir,
      droits_estimes: calculerDroitsSuccession(heir.valeur_recue, heir.type, formData.abattement_applique),
    }));

    setCalculGlobal({
      valeur_totale_collection: valeurTotale,
      droits_totaux_estimes: repartition.reduce((sum, heir) => sum + heir.droits_estimes, 0),
      repartition_par_heritier: repartition,
      dation_recommandee: valeurTotale > 150000,
    });
  };

  const fetchSuccessionPlans = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: plans, error } = await supabase
        .from("succession_plans")
        .select("*")
        .eq("client_id", session.user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSuccessionPlans(plans || []);
    } catch (error) {
      console.error("[EstimationSuccession] load plans:", error);
      toast.error(t("estimationSuccessionFiscal.alerts.loadError", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanDetails = async () => {
    const plan = successionPlans.find((item) => item.id === selectedPlanId);
    setSelectedPlan(plan || null);
    setHeritiers(Array.isArray(plan?.beneficiaries) ? plan.beneficiaries : []);

    try {
      const { data: items, error } = await supabase
        .from("succession_plan_items")
        .select(`*, artworks:artwork_id (id, title, artist, estimated_value, medium)`)
        .eq("plan_id", selectedPlanId);

      if (error) throw error;
      setPlanItems(items || []);
      calculerRepartition(items || [], plan || {});
    } catch (error) {
      console.error("[EstimationSuccession] load items:", error);
      toast.error(t("estimationSuccessionFiscal.alerts.loadError", { message: error.message }));
    }
  };

  useEffect(() => {
    fetchSuccessionPlans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPlanId) fetchPlanDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId]);

  const genererRapportPDF = () => {
    const doc = new jsPDF();
    let y = 20;

    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text(t("estimationSuccessionFiscal.pdf.title"), 20, y);
    y += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(t("estimationSuccessionFiscal.pdf.plan", { name: selectedPlan?.plan_name || "—" }), 20, y);
    y += 6;
    doc.text(t("estimationSuccessionFiscal.pdf.date", { date: formatDate() }), 20, y);
    y += 10;

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(t("estimationSuccessionFiscal.pdf.valuation"), 20, y);
    y += 8;

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    doc.text(t("estimationSuccessionFiscal.pdf.totalValue", { value: formatCurrency(calculGlobal.valeur_totale_collection) }), 25, y);
    y += 6;
    doc.text(t("estimationSuccessionFiscal.pdf.estimatedTaxes", { value: formatCurrency(calculGlobal.droits_totaux_estimes) }), 25, y);
    y += 12;

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(t("estimationSuccessionFiscal.pdf.distribution"), 20, y);
    y += 8;

    calculGlobal.repartition_par_heritier.forEach((heir) => {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(heir.name, 25, y);
      y += 6;
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(t("estimationSuccessionFiscal.pdf.receivedValue", { value: formatCurrency(heir.valeur_recue) }), 27, y);
      y += 5;
      doc.text(t("estimationSuccessionFiscal.pdf.taxes", { value: formatCurrency(heir.droits_estimes) }), 27, y);
      y += 5;
      doc.text(t("estimationSuccessionFiscal.pdf.artworks", { count: heir.oeuvres.length }), 27, y);
      y += 10;
    });

    return doc;
  };

  const enregistrerSimulation = async () => {
    if (!selectedPlanId) {
      toast.warning(t("estimationSuccessionFiscal.alerts.selectPlan"));
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const doc = genererRapportPDF();
    const pdfBlob = doc.output("blob");
    const folder = `documents/succession/${selectedPlanId}`;
    const fileName = `rapport_fiscal_${Date.now()}.pdf`;
    const fullPath = `${folder}/${fileName}`;

    await supabase.storage.from("documents").remove([fullPath]);
    const { error: uploadError } = await supabase.storage.from("documents").upload(fullPath, pdfBlob);
    if (uploadError) {
      console.error(uploadError);
      toast.error(t("estimationSuccessionFiscal.alerts.uploadError"));
      return;
    }

    const { data: urlData } = supabase.storage.from("documents").getPublicUrl(fullPath);
    await supabase.from("reports").insert([{
      title: t("estimationSuccessionFiscal.reportTitle", { name: selectedPlan?.plan_name || "—" }),
      file_url: urlData?.publicUrl,
      type: "succession_fiscal",
      client_id: session.user.id,
    }]);

    toast.success(t("estimationSuccessionFiscal.alerts.generated"));
    navigate("/family-office-dashboard");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-12">
        <h1 className="text-4xl font-serif text-gray-900 mb-4">{t("estimationSuccessionFiscal.title")}</h1>
        <p className="text-lg text-gray-600 leading-relaxed">{t("estimationSuccessionFiscal.subtitle")}</p>
      </div>

      <div className="bg-white rounded-lg border p-8 mb-8">
        <h2 className="text-2xl font-serif mb-6">{t("estimationSuccessionFiscal.plan.title")}</h2>
        {loading ? (
          <div className="text-center py-8"><p className="text-gray-600">{t("estimationSuccessionFiscal.plan.loading")}</p></div>
        ) : successionPlans.length === 0 ? (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6">
            <p className="text-yellow-800">{t("estimationSuccessionFiscal.plan.empty")}</p>
          </div>
        ) : (
          <>
            <select
              className="w-full border rounded-md px-4 py-3 mb-6"
              value={selectedPlanId}
              onChange={(event) => setSelectedPlanId(event.target.value)}
            >
              <option value="">{t("estimationSuccessionFiscal.plan.choose")}</option>
              {successionPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.plan_name} ({translateStatus(plan.status)})</option>
              ))}
            </select>

            {selectedPlan && (
              <div className="bg-gray-50 p-6 rounded">
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="text-sm text-gray-600">{t("estimationSuccessionFiscal.plan.heirs")}</p><p className="text-xl font-bold">{heritiers.length}</p></div>
                  <div><p className="text-sm text-gray-600">{t("estimationSuccessionFiscal.plan.artworks")}</p><p className="text-xl font-bold">{planItems.length}</p></div>
                  <div><p className="text-sm text-gray-600">{t("estimationSuccessionFiscal.plan.status")}</p><p className="text-xl font-bold">{translateStatus(selectedPlan.status)}</p></div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedPlanId && calculGlobal.valeur_totale_collection > 0 && (
        <>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-serif mb-6">{t("estimationSuccessionFiscal.summary.title")}</h2>
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-white/10 rounded-lg p-5"><p className="text-sm text-gray-300 mb-2">{t("estimationSuccessionFiscal.summary.totalValue")}</p><p className="text-3xl font-bold">{formatCurrency(calculGlobal.valeur_totale_collection)}</p></div>
              <div className="bg-white/10 rounded-lg p-5"><p className="text-sm text-gray-300 mb-2">{t("estimationSuccessionFiscal.summary.inheritanceTax")}</p><p className="text-3xl font-bold">{formatCurrency(calculGlobal.droits_totaux_estimes)}</p></div>
              <div className="bg-white/10 rounded-lg p-5"><p className="text-sm text-gray-300 mb-2">{t("estimationSuccessionFiscal.summary.dation")}</p><p className="text-2xl font-bold">{calculGlobal.dation_recommandee ? t("estimationSuccessionFiscal.summary.recommended") : t("estimationSuccessionFiscal.summary.notNecessary")}</p></div>
            </div>
          </div>

          <div className="bg-white rounded-lg border p-8 mb-8">
            <h2 className="text-2xl font-serif mb-6">{t("estimationSuccessionFiscal.distribution.title")}</h2>
            <div className="space-y-6">
              {calculGlobal.repartition_par_heritier.map((heir, index) => (
                <div key={`${heir.heir_id || index}`} className="border rounded-lg p-6 hover:shadow-lg transition">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{heir.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("estimationSuccessionFiscal.distribution.typeAndCount", { type: translateHeirType(heir.type), count: heir.oeuvres.length })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(heir.valeur_recue)}</p>
                      <p className="text-sm text-red-600 mt-1">{t("estimationSuccessionFiscal.distribution.taxes", { value: formatCurrency(heir.droits_estimes) })}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded p-4">
                    <p className="text-xs font-medium text-gray-600 mb-2">{t("estimationSuccessionFiscal.distribution.assignedWorks")}</p>
                    <div className="space-y-2">
                      {heir.oeuvres.slice(0, 3).map((artwork, artworkIndex) => (
                        <div key={artworkIndex} className="flex justify-between text-sm">
                          <span className="text-gray-700">{artwork.title} — {artwork.artist}{artwork.affinity_score && <span className="text-blue-600 ml-2">★ {artwork.affinity_score}</span>}</span>
                          <span className="font-medium">{formatCurrency(artwork.valeur)}</span>
                        </div>
                      ))}
                      {heir.oeuvres.length > 3 && <p className="text-xs text-gray-500 pt-2">{t("estimationSuccessionFiscal.distribution.moreWorks", { count: heir.oeuvres.length - 3 })}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <button type="button" onClick={() => navigate("/family-office-dashboard")} className="text-gray-600 hover:text-gray-900">{t("estimationSuccessionFiscal.actions.back")}</button>
            <button type="button" onClick={enregistrerSimulation} className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800">{t("estimationSuccessionFiscal.actions.generatePdf")}</button>
          </div>
        </>
      )}

      <div className="mt-16 bg-black text-white rounded-lg p-10">
        <h3 className="text-2xl font-light mb-6">{t("estimationSuccessionFiscal.difference.title")}</h3>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h4 className="text-lg font-semibold mb-3 text-amber-400">{t("estimationSuccessionFiscal.difference.validationTitle")}</h4>
            <p className="text-gray-300 text-sm leading-relaxed mb-3">{t("estimationSuccessionFiscal.difference.validationIntro")}</p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• {t("estimationSuccessionFiscal.difference.personalCollection")}</li>
              <li>• {t("estimationSuccessionFiscal.difference.insuranceUnder")}</li>
              <li>• {t("estimationSuccessionFiscal.difference.firstEvaluation")}</li>
            </ul>
            <p className="text-xs text-gray-400 mt-4">{t("estimationSuccessionFiscal.difference.validationPrice")}</p>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-3 text-blue-400">{t("estimationSuccessionFiscal.difference.cnesTitle")}</h4>
            <p className="text-gray-300 text-sm leading-relaxed mb-3">{t("estimationSuccessionFiscal.difference.cnesIntro")}</p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• {t("estimationSuccessionFiscal.difference.insuranceOver")}</li>
              <li>• {t("estimationSuccessionFiscal.difference.majorEstate")}</li>
              <li>• {t("estimationSuccessionFiscal.difference.auction")}</li>
              <li>• {t("estimationSuccessionFiscal.difference.bankGuarantee")}</li>
            </ul>
            <p className="text-xs text-gray-400 mt-4">{t("estimationSuccessionFiscal.difference.cnesPrice")}</p>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/20">
          <p className="text-sm text-gray-400 italic">{t("estimationSuccessionFiscal.difference.contact")}</p>
        </div>
      </div>
    </div>
  );
}
